import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@services/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageService } from '@services/page.service';
import { NotificationsService } from '@services/notifications.service';
import { TranslationService } from '@services/translation.service';
import { CookieService } from 'ngx-cookie-service';
import { matchFormControlValidator } from '@app/validators/must-match-input.directive';
import {
  addConfirmPwRevalidation,
  CognitoPasswordPolicy,
  getNewPwValidatorsFromPolicy,
} from '@app/shared/password-form.utils';
import { requiredTrimmedValidator } from '@app/validators-form-control/required-trimmed.validator';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  standalone: false,
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  loading = false;
  passwordStrength: number = 0;
  usernameForm: FormGroup;
  passwordForm?: FormGroup;
  policies: CognitoPasswordPolicy;
  verfifyMethod;
  passwordSent = false;
  error: string;
  hasVerificationToken = false;
  darkLogoError = false;
  private readonly destroy$ = new Subject<void>();
  private confirmPwRevalidationSub?: Subscription;

  constructor(
    private userService: UserService,
    public location: Location,
    private router: Router,
    private page: PageService,
    private notifications: NotificationsService,
    private route: ActivatedRoute,
    private cookieService: CookieService,
    private fb: FormBuilder,
    private translationService: TranslationService
  ) {}

  ngOnInit() {
    this.page.updateHideMenu(true);
    this.policies = this.getDefaultPasswordPolicy();
    const snapshot = this.route.snapshot.queryParams;
    this.hasVerificationToken = !!snapshot['token'];
    const initialUsername =
      this.cookieService.get('forgot-username')?.trim() || '';
    this.usernameForm = this.fb.group({
      username: [
        initialUsername,
        [Validators.required, requiredTrimmedValidator()],
      ],
    });
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const verificationCode = params['token'];
      if (verificationCode) {
        this.hasVerificationToken = true;
        this.passwordSent = false;
        this.passwordForm = undefined;
        this.loading = true;
        const tokenForThisLoad = verificationCode;
        this.userService
          .getPasswordPolicy()
          .pipe(takeUntil(this.destroy$))
          .subscribe({
          next: data => {
            if (this.route.snapshot.queryParams['token'] !== tokenForThisLoad) {
              return;
            }
            this.policies = data;
            this.buildPasswordForm(verificationCode);
            this.loading = false;
          },
          error: () => {
            if (this.route.snapshot.queryParams['token'] !== tokenForThisLoad) {
              return;
            }
            this.loading = false;
            this.policies = this.getDefaultPasswordPolicy();
            this.buildPasswordForm(verificationCode);
          },
        });
      } else {
        this.hasVerificationToken = false;
        this.loading = false;
        this.policies = this.getDefaultPasswordPolicy();
        this.confirmPwRevalidationSub?.unsubscribe();
        this.passwordForm = undefined;
      }
    });
  }

  ngOnDestroy(): void {
    this.confirmPwRevalidationSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getDefaultPasswordPolicy(): CognitoPasswordPolicy {
    return {
      MinimumLength: 8,
      RequireUppercase: false,
      RequireLowercase: false,
      RequireNumbers: false,
      RequireSymbols: false,
    };
  }

  private buildPasswordForm(verificationCode: string): void {
    this.confirmPwRevalidationSub?.unsubscribe();
    this.ensureUsernameFormForTokenFlow();
    const newPwValidators = getNewPwValidatorsFromPolicy(this.policies);
    this.passwordForm = this.fb.group({
      verificationCode: [verificationCode || '', Validators.required],
      newPw: ['', newPwValidators],
      confirmPw: [
        '',
        [Validators.required, matchFormControlValidator('newPw')],
      ],
    });
    this.confirmPwRevalidationSub =
      addConfirmPwRevalidation(
        this.passwordForm,
        'newPw',
        'confirmPw',
        this.destroy$
      ) ?? undefined;
  }

  private ensureUsernameFormForTokenFlow(): void {
    const fromCookie =
      this.cookieService.get('forgot-username')?.trim() || '';
    const usernameCtrl = this.usernameForm.get('username');
    if (fromCookie && usernameCtrl && usernameCtrl.value !== fromCookie) {
      usernameCtrl.setValue(fromCookie);
    }
  }

  hasSetTempPassword() {
    this.notifications.popNotification(
      '',
      'Please check your email for your temporary password.'
    );
    this.router.navigate(['/login']);
  }

  startForgot() {
    const username = this.usernameForm.get('username').value?.trim() ?? '';
    this.error = '';
    this.loading = true;
    this.userService.triggerForgotPassword(username).subscribe(
      pwData => {
        this.cookieService.set('forgot-username', username, 10);
        this.passwordSent = true;
        this.loading = false;
      },
      err => {
        this.error =
          err?.code == 'LimitExceededException'
            ? this.translationService.getTranslatedContent(
                'too_many_attempts',
                'Too many attempts at this time'
              )
            : this.translationService.getTranslatedContent(
                'unknown_error',
                'Unknown Error'
              );
        this.loading = false;
      },
      () => {}
    );
  }

  submitNewPw() {
    if (!this.passwordForm) return;
    const verificationCode = this.passwordForm.get('verificationCode').value;
    const newPw = this.passwordForm.get('newPw').value;
    const username =
      this.usernameForm?.get('username')?.value?.trim() ||
      this.cookieService.get('forgot-username')?.trim();
    if (!username) {
      this.error = this.translationService.getTranslatedContent(
        'username_required',
        'Username is required.'
      );
      return;
    }
    this.error = '';
    this.loading = true;
    this.userService
      .confirmPassword(verificationCode, newPw, username)
      .subscribe({
        next: () => {
          this.loading = false;
          this.router.navigate(['/login']);
        },
        error: err => {
          this.loading = false;
          const apiError = err?.error?.error ?? err?.error?.name;
          if (apiError === 'CodeMismatchException') {
            this.error = this.translationService.getTranslatedContent(
              'error_with_code_provided._please_try_again',
              'Error with Code Provided. Please try again.'
            );
          } else {
            this.error =
              err?.error?.message ||
              err?.message ||
              this.translationService.getTranslatedContent(
                'unknown_error',
                'An error occurred. Please try again.'
              );
          }
        },
      });
  }

  routeToLogin() {
    this.router.navigate(['/login']);
  }
}
